import React, { useCallback, useEffect, useState } from 'react';
import { Search, X, RefreshCw, IndianRupee, Package, Trophy, Clock, ShoppingBag, Check } from 'lucide-react';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { marketingService } from '../../../services/api';
import MaskedMobile from '../../common/MaskedMobile';
import ConfirmationModal from '../../common/ConfirmationModal';
import TableSkeleton from '../../common/TableSkeleton';
import { StatCardRow } from '../../common/StatCard';
import { formatRawDateTime, formatDateToDdmmyyyy } from '../../../utils/format';
import { visitDurationLabel } from './farmIncentivesView';
import Snackbar from '../../common/Snackbar';
import Select from '../../common/Select';
import Tooltip from '../../common/Tooltip';
import Pagination from '../../common/Pagination';
import { pairSlabsByBand, isOpenEnded } from './incentiveSlabs';
import {
  ProjectionBasis, ExecutiveIncentiveRow, collectionBreakdown, incentiveBasisLabel,
  monthLabel, execSlabText, canBulkApprove, execKey, incentiveCalcLabel,
  PlanPeriod, planPeriods, planPeriodLabel, currentPlanPeriod, MarketingOrderRow,
} from './marketingOrdersView';
import ExecutiveIncentivesCard from './ExecutiveIncentivesCard';
import { ORDERS_PAGE_SIZE } from './useExpandedOrders';

const formatINR = (val?: number) => '₹' + Number(val || 0).toLocaleString('en-IN');

interface MarketingOrdersSummary {
  total_all_orders?: number;
  total_filtered?: number;
  pending_payment_count?: number;
  pending_submission_count?: number;
  pending_admin_approval_count?: number;
  pending_super_admin_approval_count?: number;
  pending_super_admin_rejection_count?: number;
  paid_orders?: number;
  rejected_count?: number;
  cancelled_count?: number;
  total_paid_units?: number;
  direct_paid_units?: number;
  indirect_paid_units?: number;
  cash_collected?: number;
  online_amount_collected?: number;
  // Value applied to the order that is not money through a bank rail, read
  // from the transactions. Both sit INSIDE total_amount_collected, which is
  // why that figure agrees with the leaderboard for the same orders.
  coins_amount_collected?: number;
  coupons_amount_collected?: number;
  // Whatever is in none of the named buckets — the reason a payment method
  // added later cannot silently shrink the total.
  other_amount_collected?: number;
  total_amount_collected?: number;
  total_incentives_amount?: number;
  direct_incentives_amount?: number;
  indirect_incentives_amount?: number;
  pending_incentives_amount?: number;
  direct_pending_incentives_amount?: number;
  indirect_pending_incentives_amount?: number;
  // The farm side of the same orders: what the executives who conducted the
  // visits have been paid, and what approving the rest would pay them.
  farm_incentives?: number;
  pending_farm_incentives?: number;
}



const MarketingOrdersTab: React.FC = () => {
  const { adminMobile, adminRole } = useAppSelector((s: RootState) => s.auth);
  // Admin/SuperAdmin see every executive's orders — only they can meaningfully
  // filter by who onboarded an order, since an Executive's orders are all
  // their own.
  const isOrgWide = /superadmin|admin/i.test(adminRole || '');
  const isSuperAdmin = /superadmin/i.test(adminRole || '');
  const isMarketingManager = /marketing_manager|marketing manager|manager/i.test(adminRole || '');

  const [summary, setSummary] = useState<MarketingOrdersSummary | null>(null);
  // Executive-wise, month-wise orders and incentive slabs over the same
  // filtered set as the tiles — see build_executive_breakdown on the API.
  const [execRows, setExecRows] = useState<ExecutiveIncentiveRow[]>([]);
  const [confirmExec, setConfirmExec] = useState<ExecutiveIncentiveRow | null>(null);
  const [bulkApprovingKey, setBulkApprovingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [marketingStaff, setMarketingStaff] = useState<{ mobile: string; first_name?: string; last_name?: string; role?: string }[]>([]);
  const [loadingMarketingStaff, setLoadingMarketingStaff] = useState(false);
  const [farmStaff, setFarmStaff] = useState<{ mobile: string; name?: string }[]>([]);
  const [loadingFarmStaff, setLoadingFarmStaff] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Marketing Orders only ever shows PAID orders -- there is no filter UI to
  // change this, so it's a constant rather than state (see load()'s params).
  const paymentStatus = 'PAID';
  const [withCpf, setWithCpf] = useState<'' | 'true' | 'false'>('');
  const [onboardedByMobile, setOnboardedByMobile] = useState('');
  const [farmVisitByMobile, setFarmVisitByMobile] = useState('');
  // The incentive plan period the page is divided by — orders, tiles and the
  // executive table all narrow to orders whose incentive falls in its dates
  // (approval date, else placed). '' = all periods; null = plans not loaded
  // yet, so the first load waits for the default rather than flashing every
  // period's orders.
  const [periods, setPeriods] = useState<PlanPeriod[]>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const selectedPeriod = periods.find((p) => p.plan_id === planId) || null;

  useEffect(() => {
    const fetchPlans = async () => {
      if (!adminMobile) return;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      try {
        const res = await marketingService.listIncentivePlans(adminMobile, { period: 'MONTHLY' });
        const list = planPeriods(res?.plans, today);
        setPeriods(list);
        setPlanId((cur) => (cur !== null && cur !== '' ? cur : (currentPlanPeriod(list, today)?.plan_id || (list.length > 0 ? list[0].plan_id : ''))));
      } catch (err) {
        // Not every role can list plans — fall back to every period.
        console.error('Error fetching incentive plans:', err);
        setPeriods([]);
        setPlanId((cur) => (cur !== null ? cur : ''));
      }
    };
    fetchPlans();
  }, [adminMobile]);

  useEffect(() => {
    const fetchMarketingStaff = async () => {
      if (!adminMobile || !(isOrgWide || isMarketingManager)) return;
      setLoadingMarketingStaff(true);
      try {
        const response = await marketingService.listStaff(adminMobile);
        setMarketingStaff(response?.staff || []);
      } catch (err) {
        console.error('Error fetching marketing staff:', err);
        setMarketingStaff([]);
      } finally {
        setLoadingMarketingStaff(false);
      }
    };
    fetchMarketingStaff();
  }, [adminMobile, isOrgWide, isMarketingManager]);

  useEffect(() => {
    const fetchFarmStaff = async () => {
      if (!adminMobile) return;
      setLoadingFarmStaff(true);
      try {
        const response = await marketingService.listFarmStaff(adminMobile);
        setFarmStaff(response?.staff || []);
      } catch (err) {
        console.error('Error fetching farm staff:', err);
        setFarmStaff([]);
      } finally {
        setLoadingFarmStaff(false);
      }
    };
    fetchFarmStaff();
  }, [adminMobile]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);


  const load = useCallback(async () => {
    if (!adminMobile || planId === null) return;
    setLoading(true);
    setError(null);
    try {
      // Tiles and the executive table only; an executive-month's orders are
      // fetched when its row is opened (loadExecOrders).
      const res = await marketingService.getOrdersSummary(adminMobile, {
        search: debouncedSearch || undefined,
        withCpf: withCpf || undefined,
        onboardedByMobile: onboardedByMobile || undefined,
        farmVisitByMobile: farmVisitByMobile || undefined,
        incentiveFrom: selectedPeriod?.from,
        incentiveTo: selectedPeriod?.to,
      });
      if (res?.statuscode === 200) {
        setExecRows(res.executive_incentives || []);
        setSummary({
          total_all_orders: res.total_all_orders,
          total_filtered: res.total_filtered,
          pending_payment_count: res.pending_payment_count,
          pending_submission_count: res.pending_submission_count,
          pending_admin_approval_count: res.pending_admin_approval_count,
          pending_super_admin_approval_count: res.pending_super_admin_approval_count,
          pending_super_admin_rejection_count: res.pending_super_admin_rejection_count,
          paid_orders: res.paid_orders,
          rejected_count: res.rejected_count,
          cancelled_count: res.cancelled_count,
          total_paid_units: res.total_paid_units,
          direct_paid_units: res.direct_paid_units,
          indirect_paid_units: res.indirect_paid_units,
          cash_collected: res.cash_collected,
          online_amount_collected: res.online_amount_collected,
          coins_amount_collected: res.coins_amount_collected,
          coupons_amount_collected: res.coupons_amount_collected,
          other_amount_collected: res.other_amount_collected,
          total_amount_collected: res.total_amount_collected,
          total_incentives_amount: res.total_incentives_amount,
          direct_incentives_amount: res.direct_incentives_amount,
          indirect_incentives_amount: res.indirect_incentives_amount,
          pending_incentives_amount: res.pending_incentives_amount,
          direct_pending_incentives_amount: res.direct_pending_incentives_amount,
          indirect_pending_incentives_amount: res.indirect_pending_incentives_amount,
          farm_incentives: res.farm_incentives,
          pending_farm_incentives: res.pending_farm_incentives,
        });
      } else {
        setError(res?.message || 'Failed to load orders');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [adminMobile, planId, selectedPeriod?.from, selectedPeriod?.to, debouncedSearch, withCpf, onboardedByMobile, farmVisitByMobile]);

  useEffect(() => { load(); }, [load]);

  // The orders behind one row of the executive table. The API filters on the
  // breakdown's own keys (who the incentive credits, and its incentive month),
  // so the list is exactly what the row counted, under the same page filters.
  const loadExecOrders = useCallback(async (row: ExecutiveIncentiveRow, ordersPage: number) => {
    if (!adminMobile) return { orders: [], totalPages: 1 };
    const res = await marketingService.listExecutiveOrders(adminMobile, {
      execMobile: row.exec_mobile,
      month: row.month,
      paymentStatus,
      search: debouncedSearch || undefined,
      withCpf: withCpf || undefined,
      onboardedByMobile: onboardedByMobile || undefined,
      farmVisitByMobile: farmVisitByMobile || undefined,
      incentiveFrom: selectedPeriod?.from,
      incentiveTo: selectedPeriod?.to,
      page: ordersPage,
      page_size: ORDERS_PAGE_SIZE,
    });
    if (res?.statuscode !== 200) throw new Error(res?.message || res?.detail || 'Failed to load orders');
    return { orders: (res.orders || []) as MarketingOrderRow[], totalPages: res.total_pages || 1 };
  }, [adminMobile, selectedPeriod?.from, selectedPeriod?.to, debouncedSearch, withCpf, onboardedByMobile, farmVisitByMobile]);

  const handleBulkApprove = async (row: ExecutiveIncentiveRow) => {
    if (!adminMobile) return;
    setBulkApprovingKey(execKey(row));
    try {
      const res = await marketingService.approveIncentivesBulk(adminMobile, row.exec_mobile, row.month);
      if (res?.statuscode && res.statuscode !== 200) {
        throw new Error(res.detail || res.message || 'Approval failed');
      }
      const who = row.exec_name || row.exec_mobile;
      setSnackbar(res?.complete === false
        ? {
          message: `Approved ${res.approved_count} of ${res.approved_count + res.remaining} for ${who}; stopped at ${res.failed_order_id}: ${res.error}`,
          type: 'error',
        }
        : {
          message: `Approved ${res?.approved_count ?? row.pending_orders} incentive(s) for ${who} · ${monthLabel(row.month)}`,
          type: 'success',
        });
      load();
    } catch (e: any) {
      setSnackbar({
        message: e?.response?.data?.detail || e?.message || 'Approval failed',
        type: 'error',
      });
    } finally {
      setBulkApprovingKey(null);
      setConfirmExec(null);
    }
  };

  /**
   * The "Incentive Given" cell: whether this order's incentive has actually
   * been paid out, and if so by whom and when.
   *
   * `incentive_approved` is the only field that says money moved — the
   * incentive block's CREDITED status is derived from it, but the approver and
   * the timestamp live on the order. Approval is one-way (there is no
   * un-approve endpoint), so a Yes here is permanent and worth showing with its
   * audit trail rather than as a bare tick.
   */
  const incentiveApprovalCell = (o: MarketingOrderRow) => {
    if (o.incentive_approved) {
      const by = o.incentive_approved_by_name || o.incentive_approved_by;
      const at = fmtDateTime(o.incentive_approved_at);
      return (
        <div className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
            <Check size={11} /> Given
          </span>
          {by && (
            <span className="text-slate-600 font-semibold text-[11px] whitespace-nowrap"
              title={o.incentive_approved_by_role || undefined}>
              by {by}
            </span>
          )}
          {at && <span className="text-slate-400 text-[11px] whitespace-nowrap">{at}</span>}
        </div>
      );
    }

    // Not approved: say what is actually blocking it, so "No" is actionable.
    const status = o.incentive?.status;
    const label = status === 'PROJECTED' ? 'Awaiting approval'
      : status === 'NOT_ELIGIBLE' ? 'Not eligible'
      : 'Not yet';
    const tone = status === 'PROJECTED'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';
    return (
      <div className="flex flex-col gap-0.5">
        <span className={`inline-flex self-start px-2 py-0.5 rounded-lg border text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${tone}`}>
          {label}
        </span>
        {o.incentive?.reason && (
          <Tooltip content={o.incentive.reason} className="whitespace-normal max-w-[240px] leading-snug">
            <span className="text-slate-400 text-[11px] max-w-[190px] truncate block cursor-default">
              {o.incentive.reason}
            </span>
          </Tooltip>
        )}
      </div>
    );
  };

  const detailRow = (label: string, value: React.ReactNode) => (
    <div className="px-4 py-2.5 flex items-baseline justify-between gap-4">
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{label}</span>
      <span className="text-[13px] font-semibold text-slate-800 text-right break-words">{value}</span>
    </div>
  );

  // Neo4j serializes zoned datetimes as "2026-09-17T23:20:12.813+05:30[Asia/Kolkata]" —
  // valid for Neo4j/Java tooling, but the trailing "[Asia/Kolkata]" isn't part of the
  // JS Date grammar, so new Date() on the raw value silently returns Invalid Date.
  // The offset alone is enough for JS to parse it correctly, so drop the bracket.
  const stripZoneId = (v: string) => v.replace(/\[[^\]]+\]$/, '');

  const fmtDate = (v?: string) => {
    if (!v) return '—';
    const dt = new Date(stripZoneId(v));
    if (isNaN(dt.getTime())) return v;
    return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
  };

  // Date AND time: an approval is an audited money event, so "when" means the
  // minute it happened, not just the day. Rendered in the viewer's timezone
  // from the stored offset-aware timestamp.
  const fmtDateTime = (v?: string) => {
    if (!v) return '';
    const dt = new Date(stripZoneId(v));
    if (isNaN(dt.getTime())) return v;
    return `${fmtDate(v)}, ${dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const paymentStatusStyle = (s?: string) => {
    switch (s) {
      case 'PAID': return 'bg-emerald-50 text-emerald-700';
      case 'PENDING_PAYMENT': return 'bg-amber-50 text-amber-700';
      case 'PENDING_SUBMISSION': return 'bg-slate-100 text-slate-600';
      case 'PENDING_ADMIN_VERIFICATION': return 'bg-blue-50 text-blue-700';
      case 'PENDING_SUPER_ADMIN_VERIFICATION': return 'bg-indigo-50 text-indigo-700';
      case 'PENDING_SUPER_ADMIN_REJECTION': return 'bg-rose-50 text-rose-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const paymentStatusLabel = (s?: string) => (s || '').split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') || '—';

  const getOnboardedBy = (o: MarketingOrderRow) => {
    const mobile = o.unit_placed_by_mobile || o.order_onboarded_by_mobile || o.onboarded_by_mobile || o.incentive?.credited_to_mobile;
    let name = o.order_onboarded_by_name || o.onboarded_by_name || o.incentive?.credited_to_name;
    if (!name && mobile) {
      const staff = marketingStaff.find((s) => s.mobile === mobile);
      if (staff) {
        name = [staff.first_name, staff.last_name].filter(Boolean).join(' ');
      }
    }
    return { name, mobile };
  };

  return (
    <div className="w-full space-y-6 pb-12 font-sans">
      {/* 1. Filters Bar — search on the left, date picker/clear/refresh on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-auto min-w-0 sm:min-w-[280px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
            <input
              type="text"
              placeholder="Search by Order ID, Mobile, Name, or Lead phone/name/ID"
              className="h-[42px] pl-10 pr-8 w-full text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 outline-none focus:border-[#1B4332] transition-all duration-200"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 font-bold bg-transparent border-none cursor-pointer"
                type="button"
              >
                <X size={14} />
              </button>
            )}
          </div>

        </div>

        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          {periods.length > 0 && (
            <Select
              className="w-auto h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 transition-all duration-200 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              value={planId || ''}
                onChange={(v) => setPlanId(v)}
                options={[
                  ...periods.map((p) => ({
                    value: p.plan_id,
                    label: (
                      <div className="flex flex-col text-left">
                        <span className="text-[13px] font-bold leading-tight">{p.name}</span>
                        <span className="text-[11px] font-medium leading-tight opacity-75 mt-[2px]">{formatDateToDdmmyyyy(p.from)} – {formatDateToDdmmyyyy(p.to)}</span>
                      </div>
                    )
                  })),
                ]}
                renderValue={(selected) => {
                  if (!selected || selected.value === '') return null;
                  const p = periods.find(x => x.plan_id === selected.value);
                  if (!p) return null;
                  return (
                    <div className="flex flex-col items-start text-left">
                      <span className="text-[13px] font-bold text-slate-800 leading-tight">{p.name}</span>
                      <span className="text-[11px] text-slate-500 font-medium leading-tight mt-[2px]">{formatDateToDdmmyyyy(p.from)} – {formatDateToDdmmyyyy(p.to)}</span>
                    </div>
                  );
                }}
              />
          )}
          {(search || withCpf || onboardedByMobile || farmVisitByMobile) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setWithCpf(''); setOnboardedByMobile(''); setFarmVisitByMobile('') }}
              className="flex items-center justify-center h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold"
            >
              Clear
            </button>
          )}

          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh"
            className={`flex items-center justify-center gap-2 h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* 2. Stat Cards Overview */}
      {loading && !summary ? (
        <div className="flex gap-4 mb-4 w-full overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent snap-x">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs animate-pulse min-w-[240px] shrink-0 flex-1 snap-start">
              <div className="flex items-center justify-between mb-3">
                <div className="h-3 w-24 bg-slate-200 rounded-md" />
                <div className="shrink-0 w-8 h-8 rounded-xl bg-slate-100" />
              </div>
              <div className="h-7 w-32 bg-slate-200 rounded-md mb-3" />
              <div className="pt-2 border-t border-slate-100 flex justify-between">
                <div className="h-3 w-16 bg-slate-100 rounded-md" />
                <div className="h-3 w-16 bg-slate-100 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="flex gap-4 mb-4 w-full overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent snap-x">
          {/* Combined Orders & Collections */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex flex-col justify-between min-w-[320px] shrink-0 flex-[1.5] snap-start">
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column: Orders */}
              <div className="flex flex-col">
                <div className="mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Orders</span>
                </div>
                <div className="font-black text-slate-900 text-2xl tracking-tight whitespace-nowrap">
                  {summary.total_all_orders ?? 0}
                </div>
              </div>
              
              {/* Right Column: Collected */}
              <div className="flex flex-col">
                <div className="mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Collected</span>
                </div>
                <div className="font-black text-emerald-700 text-2xl tracking-tight whitespace-nowrap">
                  {formatINR(summary.total_amount_collected)}
                </div>
              </div>
            </div>

            <div className="mt-4 border-t border-slate-100 pt-4 grid grid-cols-2 gap-6">
              {collectionBreakdown(summary).map(line => (
                <div key={line.label} className="flex flex-col">
                  <div className="text-[10px] font-medium text-slate-500 mb-0.5">{line.label}</div>
                  <strong className="text-slate-700 text-[13px] tracking-tight">{formatINR(line.value)}</strong>
                </div>
              ))}
            </div>
          </div>

          {/* Total Paid Units */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex flex-col justify-between h-full min-w-[240px] shrink-0 flex-1 snap-start">
            <div>
              <div className="mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Paid Units</span>
              </div>
              <div className="font-black text-slate-900 text-2xl tracking-tight whitespace-nowrap">
                {(summary.total_paid_units || 0).toLocaleString('en-IN')} Units
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Direct</span>
                <strong className="text-sm text-slate-700 mt-0.5">{(summary.direct_paid_units || 0).toLocaleString('en-IN')} Units</strong>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Indirect</span>
                <strong className="text-sm text-slate-700 mt-0.5">{(summary.indirect_paid_units || 0).toLocaleString('en-IN')} Units</strong>
              </div>
            </div>
          </div>

          {/* Total Incentives Credited */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex flex-col justify-between h-full min-w-[240px] shrink-0 flex-1 snap-start">
            <div>
              <div className="mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Incentives Credited</span>
              </div>
              <div className="font-black text-slate-900 text-2xl tracking-tight whitespace-nowrap">
                {formatINR(summary.total_incentives_amount)}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Direct</span>
                <strong className="text-sm text-slate-700 mt-0.5">{formatINR(summary.direct_incentives_amount)}</strong>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Indirect</span>
                <strong className="text-sm text-slate-700 mt-0.5">{formatINR(summary.indirect_incentives_amount)}</strong>
              </div>
            </div>
          </div>

          {/* Pending Incentives */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex flex-col justify-between h-full min-w-[240px] shrink-0 flex-1 snap-start">
            <div>
              <div className="mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Pending Incentives</span>
              </div>
              <div className="font-black text-slate-900 text-2xl tracking-tight whitespace-nowrap">
                {formatINR(summary.pending_incentives_amount)}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Direct</span>
                <strong className="text-sm text-slate-700 mt-0.5">{formatINR(summary.direct_pending_incentives_amount)}</strong>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Indirect</span>
                <strong className="text-sm text-slate-700 mt-0.5">{formatINR(summary.indirect_pending_incentives_amount)}</strong>
              </div>
            </div>
          </div>

        </div>
      ) : null}

      {/* 3. Executive-wise orders and incentive slabs */}
      {!error && (
        <ExecutiveIncentivesCard
          rows={execRows}
          canApprove={(r) => canBulkApprove(r, { mobile: adminMobile, isMarketingManager, isSuperAdmin })}
          approvingKey={bulkApprovingKey}
          onApprove={setConfirmExec}
          loadOrders={loadExecOrders}
          isOrgWide={isOrgWide}
          isMarketingManager={isMarketingManager}
          marketingStaff={marketingStaff}
          farmStaff={farmStaff}
          adminMobile={adminMobile || undefined}
          hasApproveAccess={isMarketingManager || isSuperAdmin}
        />
      )}

      {/* Active Filter Chips — one pill per applied filter with its own clear,
          so "why am I seeing nothing" is answerable without hunting through
          the filter bar/status pills above. */}
      {(search || withCpf || onboardedByMobile || farmVisitByMobile) && (
        <div className="flex flex-wrap gap-2 items-center px-1">
          {search && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Search:</span>
              <span className="font-bold">{search}</span>
              <button onClick={() => { setSearch('') }} className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors"><X size={11} strokeWidth={2.5} /></button>
            </div>
          )}
          {withCpf && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">CPF:</span>
              <span className="font-bold">{withCpf === 'true' ? 'Yes' : 'No'}</span>
              <button onClick={() => { setWithCpf('') }} className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors"><X size={11} strokeWidth={2.5} /></button>
            </div>
          )}
          {onboardedByMobile && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Onboarded By:</span>
              <span className="font-bold">{(() => {
                const m = marketingStaff.find(s => s.mobile === onboardedByMobile);
                return m ? ([m.first_name, m.last_name].filter(Boolean).join(' ') || m.mobile) : onboardedByMobile;
              })()}</span>
              <button onClick={() => { setOnboardedByMobile('') }} className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors"><X size={11} strokeWidth={2.5} /></button>
            </div>
          )}
          {farmVisitByMobile && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#527d60] text-white rounded-full text-[11px] shadow-sm shadow-[#527d60]/20 transition-all">
              <span className="font-medium opacity-80">Farm Visit By:</span>
              <span className="font-bold">{(() => {
                const m = farmStaff.find(s => s.mobile === farmVisitByMobile);
                return m ? (m.name || m.mobile) : farmVisitByMobile;
              })()}</span>
              <button onClick={() => { setFarmVisitByMobile('') }} className="ml-0.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-0.5 transition-colors"><X size={11} strokeWidth={2.5} /></button>
            </div>
          )}
          <button
            onClick={() => { setSearch(''); setWithCpf(''); setOnboardedByMobile(''); setFarmVisitByMobile('') }}
            className="text-[11px] font-bold text-slate-400 hover:text-slate-700 underline decoration-slate-300 underline-offset-2 transition-colors ml-2 bg-transparent border-none cursor-pointer"
          >
            Clear All Filters
          </button>
        </div>
      )}


      {/* Approve a whole executive-month. Same finality as a single order. */}
      <ConfirmationModal
        isOpen={!!confirmExec}
        title="Approve all pending incentives?"
        description={confirmExec
          ? `This credits ${formatINR(confirmExec.pending_amount)} to ${confirmExec.exec_name || confirmExec.exec_mobile} for ${confirmExec.pending_orders} order(s) in ${monthLabel(confirmExec.month)}, oldest first. Approval is final — an incentive cannot be un-approved.`
          : ''}
        confirmLabel="Yes, approve all"
        cancelLabel="Cancel"
        loading={!!confirmExec && bulkApprovingKey === execKey(confirmExec)}
        onConfirm={() => { if (confirmExec) handleBulkApprove(confirmExec); }}
        onClose={() => setConfirmExec(null)}
        details={confirmExec ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 divide-y divide-slate-200/80">
            <div className="px-4 py-3 flex items-baseline justify-between gap-4">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Pending</span>
              <span className="text-lg font-black text-emerald-700">{formatINR(confirmExec.pending_amount)}</span>
            </div>
            {detailRow('Executive', confirmExec.exec_name || confirmExec.exec_mobile)}
            {detailRow('Month', monthLabel(confirmExec.month))}
            {detailRow('Orders', `${confirmExec.pending_orders} awaiting approval`)}
            {detailRow('Direct slab', execSlabText(confirmExec.direct_slab)
              ? `${execSlabText(confirmExec.direct_slab)!.units} · ${execSlabText(confirmExec.direct_slab)!.slab}` : '—')}
            {detailRow('Indirect slab', execSlabText(confirmExec.indirect_slab)
              ? `${execSlabText(confirmExec.indirect_slab)!.units} · ${execSlabText(confirmExec.indirect_slab)!.slab}` : '—')}
          </div>
        ) : undefined}
      />

      {snackbar && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}
    </div>
  );
};

export default MarketingOrdersTab;
